import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Lets a customer switch the variant of an existing cart line without leaving
 * the cart page or cart drawer. Shopify's Ajax Cart API cannot directly swap a
 * line's variant, so this component safely adds the replacement variant first
 * and removes the original line only after the add succeeds.
 */
class CartVariantEditor extends HTMLElement {
  /** @type {CartVariantEditorData | null} */
  #data = null;

  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {HTMLElement | null} */
  #panel = null;

  /** @type {HTMLSelectElement[]} */
  #selects = [];

  /** @type {HTMLButtonElement | null} */
  #saveButton = null;

  /** @type {HTMLButtonElement | null} */
  #cancelButton = null;

  /** @type {HTMLElement | null} */
  #status = null;

  /** @type {HTMLElement | null} */
  #preview = null;

  /** @type {HTMLElement | null} */
  #previewPrice = null;

  /** @type {HTMLElement | null} */
  #previewComparePrice = null;

  /** @type {HTMLElement | null} */
  #previewStock = null;

  /** @type {HTMLTableRowElement | null} */
  #cartRow = null;

  /** @type {HTMLImageElement | null} */
  #cartImage = null;

  /** @type {HTMLAnchorElement | null} */
  #cartMediaLink = null;

  /** @type {CartVariant | null} */
  #selectedVariant = null;

  /** @type {{src: string|null, srcset: string|null, sizes: string|null, alt: string|null, href: string|null} | null} */
  #originalVisualState = null;

  /** @type {string[]} */
  #initialOptions = [];

  /** @type {boolean} */
  #busy = false;

  /** @type {boolean} */
  #initialized = false;

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#toggle = this.querySelector('[data-cart-variant-toggle]');
    this.#panel = this.querySelector('[data-cart-variant-panel]');
    this.#selects = Array.from(this.querySelectorAll('[data-cart-variant-option]'));
    this.#saveButton = this.querySelector('[data-cart-variant-save]');
    this.#cancelButton = this.querySelector('[data-cart-variant-cancel]');
    this.#status = this.querySelector('[data-cart-variant-status]');
    this.#preview = this.querySelector('[data-cart-variant-preview]');
    this.#previewPrice = this.querySelector('[data-cart-variant-preview-price]');
    this.#previewComparePrice = this.querySelector('[data-cart-variant-preview-compare-price]');
    this.#previewStock = this.querySelector('[data-cart-variant-preview-stock]');
    this.#cartRow = this.closest('.cart-items__table-row');
    this.#cartImage = this.#cartRow?.querySelector('.cart-items__media-image') || null;
    this.#cartMediaLink = this.#cartRow?.querySelector('.cart-items__media-container') || null;

    const dataNode = this.querySelector('[data-cart-variant-data]');
    if (!(dataNode instanceof HTMLScriptElement)) return;

    try {
      this.#data = JSON.parse(dataNode.textContent || '{}');
      this.#data.properties = this.#normalizeProperties(this.#data.properties);
    } catch (error) {
      console.error('[cart-variant-editor] Invalid variant data:', error);
      return;
    }

    if (!this.#data?.variants?.length || !this.#toggle || !this.#panel) return;

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    this.#updateGloballyUnavailableValues();
    this.#resolveSelectedVariant();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(target instanceof HTMLButtonElement) || !this.contains(target)) return;

    if (target.matches('[data-cart-variant-toggle]')) {
      this.#panel?.hidden ? this.#open() : this.#close(true);
      return;
    }

    if (target.matches('[data-cart-variant-cancel]')) {
      this.#close(true);
      return;
    }

    if (target.matches('[data-cart-variant-save]')) {
      void this.#save();
    }
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-cart-variant-option]')) return;

    const changedIndex = Number(select.dataset.optionIndex || 0);
    this.#resolveSelectedVariant(changedIndex);
  };

  #open() {
    if (!this.#panel || !this.#toggle || this.#busy) return;

    this.#initialOptions = this.#selects.map((select) => select.value);
    this.#captureVisualState();
    this.#panel.hidden = false;
    this.#toggle.setAttribute('aria-expanded', 'true');
    this.#setStatus('');
    this.#resolveSelectedVariant();
    this.#selects[0]?.focus({ preventScroll: true });
  }

  /** @param {boolean} restore */
  #close(restore) {
    if (!this.#panel || !this.#toggle || this.#busy) return;

    if (restore && this.#initialOptions.length === this.#selects.length) {
      this.#selects.forEach((select, index) => {
        select.value = this.#initialOptions[index] || select.value;
      });
      this.#resolveSelectedVariant();
      this.#restoreVisualState();
    }

    this.#panel.hidden = true;
    this.#toggle.setAttribute('aria-expanded', 'false');
    this.#setStatus('');
    this.#toggle.focus({ preventScroll: true });
  }

  /**
   * Resolves the exact selected combination. If the last choice creates an
   * unavailable combination, preserve that last choice and move the other
   * dropdown(s) to the nearest available combination.
   * @param {number | undefined} changedIndex
   */
  #resolveSelectedVariant(changedIndex) {
    if (!this.#data) return null;

    const selections = this.#selects.map((select) => select.value);
    let variant = this.#findExactAvailableVariant(selections);
    let adjusted = false;

    if (!variant && Number.isInteger(changedIndex)) {
      const candidates = this.#data.variants.filter(
        (candidate) => candidate.available && candidate.options[changedIndex] === selections[changedIndex]
      );

      variant = this.#pickClosestVariant(candidates, selections, /** @type {number} */ (changedIndex));

      if (variant) {
        this.#selects.forEach((select, index) => {
          select.value = variant?.options[index] ?? select.value;
        });
        adjusted = true;
      }
    }

    if (!variant) {
      variant = this.#pickClosestVariant(
        this.#data.variants.filter((candidate) => candidate.available),
        selections
      );
    }

    if (variant && adjusted) {
      this.#setStatus('That combination is unavailable, so we selected the closest available option.');
    } else if (variant) {
      this.#setStatus('');
    } else {
      this.#setStatus('This option combination is unavailable.', true);
    }

    this.#selectedVariant = variant;
    this.#renderVariantPreview(variant);

    if (this.#saveButton) {
      const unchanged = variant ? String(variant.id) === String(this.#data.sourceVariantId) : true;
      this.#saveButton.disabled = this.#busy || !variant || unchanged;
      this.#saveButton.dataset.variantId = variant ? String(variant.id) : '';
    }

    return variant;
  }

  #captureVisualState() {
    if (this.#originalVisualState) return;

    this.#originalVisualState = {
      src: this.#cartImage?.getAttribute('src') || null,
      srcset: this.#cartImage?.getAttribute('srcset') || null,
      sizes: this.#cartImage?.getAttribute('sizes') || null,
      alt: this.#cartImage?.getAttribute('alt') || null,
      href: this.#cartMediaLink?.getAttribute('href') || null,
    };
  }

  #restoreVisualState() {
    const state = this.#originalVisualState;
    if (!state) return;

    this.#restoreAttribute(this.#cartImage, 'src', state.src);
    this.#restoreAttribute(this.#cartImage, 'srcset', state.srcset);
    this.#restoreAttribute(this.#cartImage, 'sizes', state.sizes);
    this.#restoreAttribute(this.#cartImage, 'alt', state.alt);
    this.#restoreAttribute(this.#cartMediaLink, 'href', state.href);
    this.#originalVisualState = null;
  }

  /**
   * @param {Element|null} element
   * @param {string} name
   * @param {string|null} value
   */
  #restoreAttribute(element, name, value) {
    if (!element) return;
    if (value == null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  }

  /** @param {CartVariant | null} variant */
  #renderVariantPreview(variant) {
    if (this.#preview) this.#preview.hidden = !variant;

    if (this.#previewPrice) {
      this.#previewPrice.textContent = variant?.priceFormatted || '';
    }

    if (this.#previewComparePrice) {
      const hasComparePrice = Boolean(
        variant && Number(variant.compareAtPrice || 0) > Number(variant.price || 0) && variant.compareAtPriceFormatted
      );
      this.#previewComparePrice.hidden = !hasComparePrice;
      this.#previewComparePrice.textContent = hasComparePrice ? variant?.compareAtPriceFormatted || '' : '';
    }

    if (this.#previewStock) {
      this.#previewStock.textContent = variant?.available ? 'Available to order' : 'Unavailable';
      this.#previewStock.classList.toggle('cart-variant-editor__preview-stock--available', Boolean(variant?.available));
      this.#previewStock.classList.toggle('cart-variant-editor__preview-stock--unavailable', !variant?.available);
    }

    if (!variant || this.#panel?.hidden) return;

    if (this.#cartImage && variant.image) {
      this.#cartImage.src = variant.image;
      this.#cartImage.removeAttribute('srcset');
      this.#cartImage.removeAttribute('sizes');
      if (variant.imageAlt) this.#cartImage.alt = variant.imageAlt;
    }

    if (this.#cartMediaLink && variant.url) {
      this.#cartMediaLink.href = variant.url;
    }
  }

  #updateGloballyUnavailableValues() {
    if (!this.#data) return;

    this.#selects.forEach((select, optionIndex) => {
      Array.from(select.options).forEach((option) => {
        const exists = this.#data?.variants.some(
          (variant) => variant.available && variant.options[optionIndex] === option.value
        );
        option.disabled = !exists;
      });
    });
  }

  /** @param {string[]} selections */
  #findExactAvailableVariant(selections) {
    return (
      this.#data?.variants.find(
        (variant) =>
          variant.available &&
          variant.options.length === selections.length &&
          variant.options.every((option, index) => option === selections[index])
      ) || null
    );
  }

  /**
   * @param {CartVariant[]} candidates
   * @param {string[]} selections
   * @param {number | undefined} changedIndex
   */
  #pickClosestVariant(candidates, selections, changedIndex) {
    if (!candidates.length) return null;

    return [...candidates].sort((a, b) => {
      const scoreA = this.#variantMatchScore(a, selections, changedIndex);
      const scoreB = this.#variantMatchScore(b, selections, changedIndex);
      return scoreB - scoreA;
    })[0];
  }

  /**
   * @param {CartVariant} variant
   * @param {string[]} selections
   * @param {number | undefined} changedIndex
   */
  #variantMatchScore(variant, selections, changedIndex) {
    return variant.options.reduce((score, option, index) => {
      if (option !== selections[index]) return score;
      return score + (index === changedIndex ? 100 : 10);
    }, 0);
  }

  async #save() {
    if (!this.#data || !this.#saveButton || this.#busy) return;

    const targetVariantId = Number(this.#saveButton.dataset.variantId || 0);
    const targetVariant =
      this.#selectedVariant || this.#data.variants.find((variant) => Number(variant.id) === targetVariantId) || null;

    if (!targetVariantId || String(targetVariantId) === String(this.#data.sourceVariantId)) {
      this.#close(true);
      return;
    }

    this.#setBusy(true);
    this.#setStatus('Updating your item…');

    const sections = this.#getCartSectionIds();
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [
          { id: String(this.#data.lineKey), quantity: 0 },
          { merchandiseId: String(targetVariantId), quantity: Number(this.#data.quantity || 1) },
        ],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const initialCart = await this.#fetchCart();
      const sourceBefore = this.#findSourceLine(initialCart.items || []);

      if (!sourceBefore) throw new Error('This cart item could not be found. Please refresh the page and try again.');

      const targetBefore = this.#findMatchingLine(initialCart.items || [], {
        variantId: targetVariantId,
        properties: this.#data.properties,
        sellingPlanId: this.#data.sellingPlanId,
      });
      const targetQuantityBefore = Number(targetBefore?.quantity || 0);

      await this.#addReplacementVariant(targetVariantId);

      const cartAfterAdd = await this.#fetchCart();
      const sourceAfterAdd = this.#findSourceLine(cartAfterAdd.items || []);

      if (!sourceAfterAdd) {
        await this.#rollbackReplacement(targetVariantId, targetQuantityBefore);
        throw new Error('The original cart item could not be updated. Please try again.');
      }

      let finalResult;
      try {
        finalResult = await this.#removeOriginalLine(sourceAfterAdd.key, sections);
      } catch (error) {
        await this.#rollbackReplacement(targetVariantId, targetQuantityBefore);
        throw error;
      }

      const replacementLine = this.#findMatchingLine(finalResult.items || [], {
        variantId: targetVariantId,
        properties: this.#data.properties,
        sellingPlanId: this.#data.sellingPlanId,
      });
      const integrity = this.#verifyCartIntegrity(initialCart, finalResult, sourceBefore, replacementLine);
      const selectedOptions = targetVariant?.options || replacementLine?.options_with_values?.map((option) => option.value) || [];

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(finalResult),
        detail: {
          items: finalResult.items,
          itemCount: finalResult.item_count,
          sections: finalResult.sections,
          source: 'cart-variant-editor',
          sourceProductId: sourceBefore.product_id,
          targetProductId: replacementLine?.product_id || sourceBefore.product_id,
          targetVariantId,
          targetLineKey: replacementLine?.key || '',
          selectedOptions,
          confirmationMessage: `Updated to ${selectedOptions.join(' / ')}`,
          integrityWarning: integrity.warning,
          discountVerified: integrity.discountVerified,
          preserveDrawerUpsells: true,
          didError: false,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update this item.';

      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'INVALID',
        })
      );

      this.#setStatus(message, true);
      console.error('[cart-variant-editor] Variant update failed:', error);
    } finally {
      this.#setBusy(false);
      this.#resolveSelectedVariant();
    }
  }

  /** @param {number} targetVariantId */
  async #addReplacementVariant(targetVariantId) {
    if (!this.#data) return;

    /** @type {Record<string, any>} */
    const item = {
      id: targetVariantId,
      quantity: Number(this.#data.quantity || 1),
    };

    if (Object.keys(this.#data.properties || {}).length) item.properties = this.#data.properties;
    if (this.#data.sellingPlanId) item.selling_plan = Number(this.#data.sellingPlanId);

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [item] }),
    });

    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'The selected option could not be added.');
    }
  }

  /**
   * @param {string} key
   * @param {string[]} sections
   */
  async #removeOriginalLine(key, sections) {
    const response = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: key,
        quantity: 0,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });

    const result = await response.json();
    if (!response.ok || result.errors || result.status) {
      throw new Error(result.description || result.message || result.errors || 'The cart item could not be updated.');
    }

    return result;
  }

  /**
   * Restores the target line to its pre-update quantity if removing the source
   * line fails after the replacement has already been added.
   * @param {number} targetVariantId
   * @param {number} targetQuantityBefore
   */
  async #rollbackReplacement(targetVariantId, targetQuantityBefore) {
    try {
      const cart = await this.#fetchCart();
      const targetLine = this.#findMatchingLine(cart.items || [], {
        variantId: targetVariantId,
        properties: this.#data?.properties || {},
        sellingPlanId: this.#data?.sellingPlanId || null,
      });

      if (!targetLine) return;

      await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: targetLine.key,
          quantity: targetQuantityBefore,
        }),
      });
    } catch (rollbackError) {
      console.error('[cart-variant-editor] Rollback failed:', rollbackError);
    }
  }

  /**
   * Confirms that changing a variant did not alter the product quantity,
   * line properties, selling plan, or an existing discount unexpectedly.
   * @param {any} initialCart
   * @param {any} finalCart
   * @param {any} sourceLine
   * @param {any} replacementLine
   */
  #verifyCartIntegrity(initialCart, finalCart, sourceLine, replacementLine) {
    const sourceProductId = Number(sourceLine?.product_id || 0);
    const quantityBefore = this.#productQuantity(initialCart?.items || [], sourceProductId);
    const quantityAfter = this.#productQuantity(finalCart?.items || [], sourceProductId);
    const propertiesPreserved =
      replacementLine &&
      this.#stableStringify(this.#normalizeProperties(replacementLine.properties)) ===
        this.#stableStringify(this.#normalizeProperties(this.#data?.properties));
    const expectedPlan = this.#data?.sellingPlanId ? String(this.#data.sellingPlanId) : '';
    const actualPlan = replacementLine?.selling_plan_allocation?.selling_plan?.id
      ? String(replacementLine.selling_plan_allocation.selling_plan.id)
      : '';
    const sellingPlanPreserved = expectedPlan === actualPlan;

    const sourceHadLineDiscount = Boolean(sourceLine?.line_level_discount_allocations?.length);
    const cartHadCartDiscount = Boolean(initialCart?.cart_level_discount_applications?.length);
    const replacementHasLineDiscount = Boolean(replacementLine?.line_level_discount_allocations?.length);
    const cartHasCartDiscount = Boolean(finalCart?.cart_level_discount_applications?.length);
    const discountVerified =
      (!sourceHadLineDiscount || replacementHasLineDiscount) && (!cartHadCartDiscount || cartHasCartDiscount);

    let warning = '';
    if (!replacementLine || quantityBefore !== quantityAfter || !propertiesPreserved || !sellingPlanPreserved) {
      warning = 'Your item was updated, but please refresh your bag to confirm all set details.';
    } else if (!discountVerified) {
      warning = 'Your item was updated. Please check that your set discount is still shown before checkout.';
    }

    return { warning, discountVerified };
  }

  /** @param {Array<any>} items @param {number} productId */
  #productQuantity(items, productId) {
    return items.reduce((total, item) => {
      return Number(item.product_id) === productId ? total + Number(item.quantity || 0) : total;
    }, 0);
  }

  async #fetchCart() {
    const cartItemsComponent = this.closest('cart-items-component');

    if (cartItemsComponent && typeof cartItemsComponent.fetchCartData === 'function') {
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error('Unable to load the cart.');
    return response.json();
  }

  /** @param {Array<any>} items */
  #findSourceLine(items) {
    if (!this.#data) return null;

    return (
      items.find((item) => item.key === this.#data?.lineKey) ||
      this.#findMatchingLine(items, {
        variantId: Number(this.#data.sourceVariantId),
        properties: this.#data.properties,
        sellingPlanId: this.#data.sellingPlanId,
      })
    );
  }

  /**
   * @param {Array<any>} items
   * @param {{ variantId: number, properties: Record<string, any>, sellingPlanId: number|string|null }} match
   */
  #findMatchingLine(items, match) {
    const expectedProperties = this.#stableStringify(this.#normalizeProperties(match.properties));
    const expectedPlan = match.sellingPlanId ? String(match.sellingPlanId) : '';

    return (
      items.find((item) => {
        const itemPlan = item.selling_plan_allocation?.selling_plan?.id
          ? String(item.selling_plan_allocation.selling_plan.id)
          : '';

        return (
          Number(item.variant_id || item.id) === Number(match.variantId) &&
          this.#stableStringify(this.#normalizeProperties(item.properties)) === expectedProperties &&
          itemPlan === expectedPlan
        );
      }) || null
    );
  }

  /** @param {any} value */
  #normalizeProperties(value) {
    if (!value) return {};
    if (Array.isArray(value)) return Object.fromEntries(value);
    if (typeof value === 'object') return value;
    return {};
  }

  /** @param {Record<string, any>} object */
  #stableStringify(object) {
    return JSON.stringify(
      Object.keys(object || {})
        .sort()
        .reduce((result, key) => {
          result[key] = object[key];
          return result;
        }, /** @type {Record<string, any>} */ ({}))
    );
  }

  #getCartSectionIds() {
    const ids = new Set();

    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) ids.add(component.dataset.sectionId);
    });

    if (!ids.size) ids.add(this.closest('cart-items-component')?.dataset.sectionId || 'cart-drawer-section');
    return Array.from(ids).slice(0, 5);
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    this.toggleAttribute('aria-busy', busy);

    this.#selects.forEach((select) => {
      select.disabled = busy;
    });

    if (this.#toggle) this.#toggle.disabled = busy;
    if (this.#cancelButton) this.#cancelButton.disabled = busy;

    if (this.#saveButton) {
      this.#saveButton.disabled = busy;
      const label = this.#saveButton.querySelector('[data-cart-variant-save-label]');
      if (label) label.textContent = busy ? 'Updating…' : 'Update';
    }
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setStatus(message, isError = false) {
    if (!this.#status) return;
    this.#status.textContent = message;
    this.#status.classList.toggle('cart-variant-editor__status--error', isError);
  }
}

if (!customElements.get('cart-variant-editor')) {
  customElements.define('cart-variant-editor', CartVariantEditor);
}

/**
 * @typedef {Object} CartVariant
 * @property {number|string} id
 * @property {boolean} available
 * @property {string[]} options
 * @property {number} [price]
 * @property {string} [priceFormatted]
 * @property {number} [compareAtPrice]
 * @property {string} [compareAtPriceFormatted]
 * @property {string|null} image
 * @property {string} [imageAlt]
 * @property {string} [url]
 */

/**
 * @typedef {Object} CartVariantEditorData
 * @property {string} lineKey
 * @property {number|string} sourceVariantId
 * @property {number} quantity
 * @property {Record<string, any>|Array<[string, any]>} properties
 * @property {number|string|null} sellingPlanId
 * @property {string[]} options
 * @property {CartVariant[]} variants
 */
